import { HttpClient } from '@angular/common/http';
import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import _ from 'lodash';
import { HostModeService } from '@shared/services/hostMode.service';

import { ToastrService } from '@shared/services/toastr.service';
import { SitesService } from '@shared/services/sites/sites.service';
import { AuthService } from '@shared/services/auth.service';
import { AdminOrganizationService } from '@shared/services/admin-organization.service';
import { AdminClusterService, Cluster, ClusterData } from '@shared/services/admin-cluster.service';
import { PageRequest, SpringPage } from '@shared/page';
import { distinctUntilChanged, fromEvent, Subject, switchMap } from 'rxjs';
import { debounceTime, map } from 'rxjs/operators';
import { trimToNull } from '@shared/utils/strings';
import { OgoResponseItems } from '@shared/services/ogo-response';
import { EditMode } from '@shared/edit.mode';
import { createInvalidObservableTypeError } from 'rxjs/internal/util/throwUnobservableError';
import { ADMIN_PREFIX } from '../../../app-routing.constants';
import { ORGANIZATIONS_ROUTE } from '../admin-routing.module';
import { NgClass, NgFor, NgIf } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { BadgeOrganizationComponent } from '@shared/components/badge-organization/badge-organization.component';
import { NgxPaginationModule } from 'ngx-pagination';
import { NgSelectComponent } from '@ng-select/ng-select';
import { TrimToNullDirective } from '@shared/directives/trim-to-null.directive';
import { SwitchComponent } from '@shared/components/switch/switch.component';
import { ModalBasicComponent } from '@shared/components/modal-basic/modal-basic.component';
import { TranslatePipe } from '@ngx-translate/core';
import { ClusterUidAndName } from '@shared/models/cluster';

@Component({
  selector: 'app-cluster',
  templateUrl: './cluster.component.html',
  styleUrls: ['./cluster.component.scss'],
  imports: [
    NgClass,
    FormsModule,
    NgIf,
    NgFor,
    RouterLink,
    BadgeOrganizationComponent,
    NgxPaginationModule,
    NgSelectComponent,
    TrimToNullDirective,
    SwitchComponent,
    ModalBasicComponent,
    TranslatePipe,
  ],
})
export class ClusterComponent implements OnInit {
  lang: string;

  user: any = {};
  editMode: EditMode;
  cluster: EditableCluster;
  template: any = {};
  clusterPage: SpringPage<Cluster>;
  pageRequest: PageRequest = { size: 20, number: 0 };
  clustersTwin: ClusterUidAndName[] = [];
  clusterTypes: any = [];
  elasticsearchs: any = [];
  organizations: any = [];
  viewMode: ViewMode = 'list';
  filter = '';

  selectedCluster: Cluster;
  @ViewChild('search') searchInputRef: ElementRef;
  @ViewChild('modalDelete') modalDelete: any;

  triggerLoadPage = new Subject<void>();

  constructor(
    private http: HttpClient,
    private adminClusterService: AdminClusterService,
    private sites: SitesService,
    private auth: AuthService,
    private toastr: ToastrService,
    public hostModeService: HostModeService,
    private adminOrganizationService: AdminOrganizationService,
  ) {}

  ngOnInit() {
    this.user = this.auth.user;
    this.loadClusterTypes();
    this.loadElasticsearchs();
  }

  ngAfterViewInit(): void {
    this.triggerLoadPage.pipe(switchMap(() => this.getPage())).subscribe((page) => {
      this.clusterPage = page;
    });

    fromEvent(this.searchInputRef.nativeElement, 'keyup')
      .pipe(
        debounceTime(300),
        map(() => this.searchInputRef.nativeElement.value as string),
        distinctUntilChanged(),
      )
      .subscribe(() => this.newSearch());

    this.loadPage();
  }

  async loadOrganizations(searchTerm: string) {
    if (searchTerm.length >= 2) {
      let res: any = null;
      try {
        res = await this.adminOrganizationService.getOrganizations({ searchTerm });
      } catch (error) {
        return this.toastr.error(error.error);
      }

      res.content = res.content.map((o) => {
        return { label: o.name + ' - ' + o.code, ...o };
      });
      this.organizations = res.content;
    }
  }

  editCluster(cluster?: Cluster) {
    this.viewMode = 'detail';
    this.clustersTwin = this.clusterPage.content.filter((c) => c.uid != cluster?.uid);
    if (cluster) {
      this.editMode = 'update';
      this.cluster = _.clone(cluster);
      this.organizations = [{ label: cluster.owner.name + '-' + cluster.owner.code, code: cluster.owner.code }];
      this.cluster.ownerCode = this.cluster.owner?.code;
      this.cluster.twinUid = this.cluster.twin?.uid;
      this.cluster.typeId = this.cluster.type?.id;
      this.cluster.elasticsearchId = this.cluster.elasticsearch?.id;
    } else {
      this.editMode = 'create';
      this.cluster = {
        supportsCache: false,
        supportsIpv6Origins: false,
        supportsMtls: false,
        supportsChallenge: false,
      };
    }
  }

  newSearch() {
    this.pageRequest.number = 0;
    this.loadPage();
  }

  loadPage() {
    this.triggerLoadPage.next();
  }

  changePage(pageNumber: number) {
    this.pageRequest.number = pageNumber - 1;
    this.loadPage();
  }

  getPage() {
    return this.adminClusterService.getPage({ filter: trimToNull(this.filter) }, this.pageRequest);
  }

  loadElasticsearchs() {
    this.http
      .post<OgoResponseItems<any>>('elasticsearch/get', {
        data: {},
      })
      .subscribe((res) => {
        if (!res.hasError) {
          this.elasticsearchs = res.items.map((es) => ({ ...es, label: `${es.nom} -- ${es.endpoint}` }));
        }
      });
  }

  loadClusterTypes() {
    this.http
      .post('clusterType/get', {
        data: {},
      })
      .subscribe((res: any) => {
        if (!res.hasError) {
          this.clusterTypes = res.items;
        }
      });
  }

  onCreateCluster(cluster: EditableCluster) {
    try {
      this.validate(cluster);
    } catch (e) {
      return;
    }

    this.adminClusterService.create(this.buildClusterData(cluster)).subscribe((res) => {
      this.toastr.success('EnregistrementReussi');
      this.refreshList();
    });
  }

  onUpdateCluster(cluster: EditableCluster) {
    try {
      this.validate(cluster);
    } catch (e) {
      return;
    }
    this.adminClusterService.patch(cluster.uid, this.buildClusterData(cluster)).subscribe((res: any) => {
      this.toastr.success('MiseAJourReussie');
      this.refreshList();
    });
  }

  private refreshList() {
    this.loadPage();
    this.sites.purgeContext();
    this.setListMode();
  }

  setListMode() {
    this.cluster = undefined;
    this.editMode = undefined;
    this.viewMode = 'list';
  }

  deleteCluster(cluster: Cluster) {
    this.adminClusterService.delete(cluster.uid).subscribe((res) => {
      this.toastr.success();
      this.loadPage();
    });
  }

  buildClusterData(cluster: ValidatedCluster): ClusterData {
    const { ownerCode, twinUid, elasticsearchId, typeId, ...other } = cluster;
    return {
      ...other,
      owner: ownerCode ? { code: ownerCode } : null,
      type: typeId ? { id: typeId } : null,
      twin: twinUid ? { uid: twinUid } : null,
      elasticsearch: elasticsearchId ? { id: elasticsearchId } : null,
    };
  }

  validate(cluster: EditableCluster): asserts cluster is ValidatedCluster {
    let errors = [];
    if (cluster.name === undefined || cluster.name.trim() === '') {
      errors.push('Saisir le libellé');
    }
    if (cluster.uid === undefined || cluster.uid.trim() === '') {
      errors.push("Saisir l'uid");
    }
    if (cluster.ip === undefined || cluster.ip.trim() === '') {
      errors.push("Saisir l'adresse IP");
    }
    if (cluster.endpoint === undefined || cluster.endpoint.trim() === '') {
      errors.push("Saisir l'endpoint");
    }
    if (cluster.defaultSiteMode === undefined || cluster.defaultSiteMode.trim() === '') {
      errors.push('Saisir le mode');
    }

    if (!cluster.typeId) {
      errors.push('Sélectionner un template');
    }

    if (!cluster.ownerCode) {
      errors.push('Sélectionner un propriétaire');
    }

    if (!cluster.elasticsearchId) {
      errors.push('Sélectionner un Elasticsearch');
    }

    if (errors.length > 0) {
      errors.forEach((error) => this.toastr.error(error));
      throw new Error();
    }
  }

  showModalDelete(cluster: Cluster) {
    this.selectedCluster = cluster;
    this.modalDelete.show();
  }

  hideModalDelete() {
    this.selectedCluster = null;
    this.modalDelete.hide();
  }

  protected readonly createInvalidObservableTypeError = createInvalidObservableTypeError;
  protected readonly ADMIN_PREFIX = ADMIN_PREFIX;
  protected readonly ORGANIZATIONS_ROUTE = ORGANIZATIONS_ROUTE;
}

type EditableCluster = Partial<ValidatedCluster>;

type ValidatedCluster = Cluster & {
  ownerCode: string;
  twinUid?: string;
  elasticsearchId: number;
  typeId: number;
};

type ViewMode = 'list' | 'detail';
